import { useState, useRef, useCallback, useEffect } from 'react';
import { HttpMethod, initFetch } from '../utils/fetcher';

const DEFAULT_FETCH_OPTIONS = {};

/**
 * Hook générique interne pour l'appel d'une API (GET, POST, etc.).
 * Non exporté : utiliser useApiGet, useApiPost, useApiPut, useApiPatch, useApiDelete.
 *
 * @param {string} uri - Chemin de la ressource, ex: "utilisateurs/1".
 * @param {HttpMethod} method - Méthode HTTP. HttpMethod.GET par défaut.
 * @param {object} [fetchOptions] - Options relayées à initFetch (voir utils/fetcher.js).
 * @param {boolean} [fetchOptions.Authorization=true] - Ajoute le header
 *   `Authorization : Bearer <token>` (token lu dans le store Redux). Valeur à
 *   positionner à `false` pour un appel public ou anonyme (ex: authentification,
 *   route ne nécessitant pas de jeton).
 * @param {boolean} [fetchOptions.formData=false] - À positionner à `true` pour
 *   un envoi de fichier. Modifie le header transmis (absence de `Content-Type:
 *   application/json`, le boundary multipart étant défini par le navigateur) et
 *   transmet le payload de `execute()` sans transformation (sans JSON.stringify).
 *   Un objet `FormData` doit alors être fourni en argument de `execute`.
 * @param {boolean} [fetchOptions.pdf=false] - À positionner à `true` pour cibler
 *   l'hôte de fichiers statiques/PDF (`pdfUri`) plutôt que l'API applicative
 *   (`baseUri`).
 *
 * Remarque d'implémentation : fetchOptions constitue une dépendance du
 * useCallback interne de `execute`. Un objet littéral fourni en ligne
 * (ex: `useApiGet(uri, { pdf: true })`) est recréé à chaque rendu du
 * composant appelant, ce qui entraîne la recréation de `execute` à chaque
 * rendu. Il est recommandé de déclarer fetchOptions :
 *   - en constante hors composant lorsque les valeurs sont fixes :
 *       const PDF_OPTIONS = { pdf: true };
 *       const { execute } = useApiGet(uri, PDF_OPTIONS);
 *   - via useMemo lorsque les valeurs dépendent de props ou de state :
 *       const options = useMemo(() => ({ pdf: true }), []);
 *       const { execute } = useApiGet(uri, options);
 */
function useApi(uri, method = HttpMethod.GET, fetchOptions = DEFAULT_FETCH_OPTIONS) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const abortControllerRef = useRef(null);

  const execute = useCallback(async (payload) => {
    // Annulation de toute requête en cours pour ce hook avant l'envoi d'une nouvelle
    abortControllerRef.current?.abort();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const { url, params } = initFetch(uri, method, fetchOptions);

      const requestInit = { ...params, signal: controller.signal };

      // Le body n'est ajouté que pour les méthodes qui en font usage
      const methodsWithBody = [HttpMethod.POST, HttpMethod.PUT, HttpMethod.PATCH];
      if (payload !== undefined && methodsWithBody.includes(method)) {
        // Cas formData : le payload (objet FormData) est transmis sans transformation
        requestInit.body = fetchOptions.formData ? payload : JSON.stringify(payload);
      }
      //console.log('requestInit', requestInit);
      const response = await fetch(url, requestInit);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const text = await response.text();
      const result = text ? JSON.parse(text) : null;

      // Le résultat n'est appliqué que si aucune requête plus récente n'a été déclenchée entretemps
      if (abortControllerRef.current === controller) {
        setData(result);
      }

      return result;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        setData(null);
      }
      return null;
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
        abortControllerRef.current = null;
      }
    }
  }, [uri, method, fetchOptions]);

  const abortRequest = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const abort = useCallback(() => {
    abortRequest();
    setData(null);
    setLoading(false);
    setError(null);
  }, [abortRequest]);

  // Annulation de la requête en cours au démontage du composant
  useEffect(() => abortRequest, [abortRequest]);

  return { execute, abort, data, loading, error };
}

// Hooks exportés, un par méthode HTTP. useApi n'est pas exposé hors de ce module.
// Chaque hook accepte en second paramètre les mêmes fetchOptions :
//   { Authorization?: boolean, formData?: boolean, pdf?: boolean }
// Le détail de chaque option est documenté dans la JSDoc de useApi ci-dessus.

/** Requête GET. fetchOptions: { Authorization, formData, pdf } */
export function useApiGet(uri, fetchOptions = DEFAULT_FETCH_OPTIONS) {
  return useApi(uri, HttpMethod.GET, fetchOptions);
}

/** Requête POST. fetchOptions: { Authorization, formData, pdf } */
export function useApiPost(uri, fetchOptions = DEFAULT_FETCH_OPTIONS) {
  return useApi(uri, HttpMethod.POST, fetchOptions);
}

/** Requête PUT. fetchOptions: { Authorization, formData, pdf } */
export function useApiPut(uri, fetchOptions = DEFAULT_FETCH_OPTIONS) {
  return useApi(uri, HttpMethod.PUT, fetchOptions);
}

/** Requête PATCH. fetchOptions: { Authorization, formData, pdf } */
export function useApiPatch(uri, fetchOptions = DEFAULT_FETCH_OPTIONS) {
  return useApi(uri, HttpMethod.PATCH, fetchOptions);
}

/**
 * Requête DELETE. fetchOptions: { Authorization, formData, pdf }
 * L'option formData est sans effet ici : DELETE ne transmet jamais de body
 * (cf. la liste methodsWithBody dans useApi).
 */
export function useApiDelete(uri, fetchOptions = DEFAULT_FETCH_OPTIONS) {
  return useApi(uri, HttpMethod.DELETE, fetchOptions);
}